// Donor quick view as a bottom sheet — used from the dashboard, where opening a donor should not
// navigate away from the figures you are reading. Shows contact, totals and the latest donations,
// with a link to the full profile page (/donors/:id) for everything else.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Clock, Mail, MessageCircle, Phone, SquareArrowOutUpRight, User } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fetchDonor, formatAmount, type DonorProfile } from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { useSessionScope } from "@/lib/session-scope";
import { emailAddress, telNumber, whatsappNumber } from "@/lib/contact";
import { EmailValue, PhoneValue } from "@/components/contact-value";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export function DonorSheet({
  donorId,
  budgetYearId,
  causeId,
  onClose,
}: {
  /** null → closed. */
  donorId: number | null;
  budgetYearId: number | null;
  causeId: number | null;
  onClose: () => void;
}) {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const [data, setData] = useState<DonorProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!token || !scope || donorId == null) return;
    const controller = new AbortController();
    fetchDonor(token, scope, donorId, { budgetYearId, causeId }, controller.signal)
      .then(setData)
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(
          e instanceof ApiError && e.status === 403
            ? 'Donor details need the "View donation" permission.'
            : (e as Error).message
        );
      });
    return () => controller.abort();
  }, [token, scope, donorId, budgetYearId, causeId]);

  const donor = data?.donor;
  const tel = telNumber(donor?.mobile);
  const wa = whatsappNumber(donor?.whatsAppNumber ?? donor?.mobile);
  const mail = emailAddress(donor?.email);

  return (
    <Sheet open={donorId != null} onOpenChange={(open) => !open && onClose()}>
      {/* Fixed-height panel: the header stays put, only the body below it scrolls. */}
      <SheetContent side="bottom" className="flex max-h-[85svh] flex-col overflow-hidden pb-safe">
        <SheetHeader className="shrink-0 border-b border-border pr-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="flex items-center gap-2">
                {donor?.isOrganization ? (
                  <Building2 className="size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <User className="size-4 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate">{donor?.name ?? "Donor"}</span>
              </SheetTitle>
              <SheetDescription className="truncate">
                {[donor?.mobile, donor?.email].filter(Boolean).join(" · ") ||
                  (data ? "No contact details" : "Loading…")}
              </SheetDescription>
            </div>
            {donor && (
              <Button
                nativeButton={false}
                variant="outline"
                size="sm"
                className="mt-0.5 shrink-0"
                // Icon-only on the narrowest screens, so it still needs a name.
                render={<Link to={`/donors/${donor.id}`} aria-label="Open full profile" title="Open full profile" />}
              >
                <SquareArrowOutUpRight /> <span className="hidden sm:inline">Full profile</span>
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 pb-4">
          {error ? (
            <p className="py-6 text-center text-sm text-destructive">{error}</p>
          ) : !data ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-16" />
              <Skeleton className="h-24" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {tel && (
                  <Button nativeButton={false} variant="outline" size="sm" render={<a href={`tel:${tel}`} />}>
                    <Phone /> Call
                  </Button>
                )}
                {wa && (
                  <Button
                    nativeButton={false}
                    variant="outline"
                    size="sm"
                    render={<a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" />}
                  >
                    <MessageCircle /> WhatsApp
                  </Button>
                )}
                {mail && (
                  <Button nativeButton={false} variant="outline" size="sm" render={<a href={`mailto:${mail}`} />}>
                    <Mail /> Email
                  </Button>
                )}
              </div>

              {donor?.contactNotes && (
                <p className="text-xs text-amber-700 dark:text-amber-400">{donor.contactNotes}</p>
              )}

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label={`FY ${data.budgetYear.name}`} value={formatAmount(data.totals.fy.amount)} sub={`${data.totals.fy.count} donations`} />
                <Stat label="All years here" value={formatAmount(data.totals.lifetime.amount)} sub={`${data.totals.lifetime.count} donations`} />
                <Stat label="First donation" value={formatDate(data.totals.firstDonation)} />
                <Stat label="Last donation" value={formatDate(data.totals.lastDonation)} />
              </div>

              {/* Who they are */}
              <Facts
                rows={[
                  ["Date of birth", donor?.dob ? `${formatDate(donor.dob)}${donor.dobAuthenticity ? ` (${donor.dobAuthenticity})` : ""}` : null],
                  ["Type", donor?.type],
                  ["Center", donor?.center],
                  ["Satsanga", donor?.satsangas],
                  ["PAN", donor?.pan ? `${donor.pan}${donor.panVerified ? " (verified)" : ""}` : null],
                  ["Referred by", donor?.referredBy],
                ]}
              />

              {/* How to reach them */}
              <Group title="Contact">
                <Facts
                  rows={[
                    [
                      "Mobile",
                      data.contacts.mobiles.length ? (
                        <span className="flex flex-col items-end gap-0.5">
                          {data.contacts.mobiles.map((m, i) => (
                            <PhoneValue key={i} value={m.number + (m.location ? ` (${m.location})` : "")} />
                          ))}
                        </span>
                      ) : null,
                    ],
                    ["WhatsApp", donor?.whatsAppNumber ? <PhoneValue value={donor.whatsAppNumber} /> : null],
                    [
                      "Email",
                      data.contacts.emails.length ? (
                        <span className="flex flex-col items-end gap-0.5">
                          {data.contacts.emails.map((e, i) => (
                            <EmailValue key={i} value={e.email} />
                          ))}
                        </span>
                      ) : null,
                    ],
                    [
                      "Landline",
                      data.contacts.phones.length ? (
                        <span className="flex flex-col items-end gap-0.5">
                          {data.contacts.phones.map((ph, i) => (
                            <PhoneValue key={i} value={ph.number} />
                          ))}
                        </span>
                      ) : null,
                    ],
                  ]}
                />
              </Group>

              {data.addresses.length > 0 && (
                <Group title="Address">
                  <ul className="flex flex-col gap-2 text-sm">
                    {data.addresses.map((ad) => (
                      <li key={ad.id}>
                        <span className="text-xs text-muted-foreground">
                          {ad.location ?? "Address"}
                          {ad.isPrimary ? " · primary" : ""}
                        </span>
                        <div>{[ad.lines, ad.city, ad.state, ad.zip, ad.country].filter(Boolean).join(", ")}</div>
                      </li>
                    ))}
                  </ul>
                </Group>
              )}

              {/* Always shown: "none recorded" is itself worth knowing (a date to collect). */}
              <Group title="Birthdays & anniversaries">
                {data.anniversaries.length === 0 && (
                  <p className="py-2 text-sm text-muted-foreground">None recorded.</p>
                )}
                {data.anniversaries.length > 0 && (
                  <div className="flex flex-col">
                    {data.anniversaries.map((an, i) => (
                      <div
                        key={i}
                        className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 text-sm last:border-0"
                      >
                        <span className="shrink-0 text-muted-foreground">{an.type ?? "Anniversary"}</span>
                        <span className="min-w-0 text-right">
                          {formatDate(an.date)}
                          {an.remarks && (
                            <span className="block truncate text-xs text-muted-foreground">{an.remarks}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Group>

              {data.recurringPlans.length > 0 && (
                <Group title="Recurring plans">
                  <div className="flex flex-col">
                    {data.recurringPlans.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 text-sm last:border-0"
                      >
                        <span className="min-w-0 truncate text-muted-foreground">
                          {r.cause ?? "—"}
                          {!r.active && " (inactive)"}
                        </span>
                        <span className="shrink-0 text-right">
                          <span className="font-medium tabular-nums">{formatAmount(r.amount)}</span>
                          <span className="block text-xs text-muted-foreground">{r.repeats}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </Group>
              )}

              <Group title="Previous donations">
                {data.donations.length === 0 ? (
                  <p className="py-2 text-sm text-muted-foreground">None in this scope.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.donations.slice(0, 10).map((d) => (
                      <li key={d.id} className="flex items-center gap-3 py-2 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{d.causes || d.receiptNumber || "Donation"}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {[formatDate(d.date), d.receiptNumber, d.modeOfPayment].filter(Boolean).join(" · ")}
                          </div>
                          {d.softCredits.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Soft credit:{" "}
                              {d.softCredits
                                .map((sc) =>
                                  [sc.name, sc.type ? `(${sc.type})` : null, sc.amount ? formatAmount(sc.amount) : null]
                                    .filter(Boolean)
                                    .join(" ")
                                )
                                .join(", ")}
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {!d.received && (
                            <Badge variant="outline" className="gap-1 text-[10px]">
                              <Clock className="size-3" /> Pending
                            </Badge>
                          )}
                          <span className="font-medium tabular-nums">{formatAmount(d.amount)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {data.donations.length > 10 && (
                  <Link to={`/donors/${donor?.id}`} className="mt-1 inline-block text-xs text-primary">
                    View all {data.totals.lifetime.count} donations
                  </Link>
                )}
              </Group>

              {data.donorNotes.length > 0 && (
                <Group title="Notes">
                  <ul className="flex flex-col gap-1 text-sm">
                    {data.donorNotes.slice(0, 3).map((n) => (
                      <li key={n.id}>
                        <div className="whitespace-pre-wrap">{n.note}</div>
                        <div className="text-xs text-muted-foreground">
                          {[formatDate(n.date), n.by].filter(Boolean).join(" · ")}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Group>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** A titled block inside the sheet. */
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">{title}</div>
      {children}
    </div>
  );
}

/**
 * One field per line, label left and value right — the usual phone settings/detail layout.
 * Empty values are dropped.
 */
function Facts({ rows }: { rows: [string, React.ReactNode][] }) {
  const shown = rows.filter(([, v]) => v != null && v !== "");
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-col">
      {shown.map(([label, value]) => (
        <div
          key={label}
          className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 text-sm last:border-0"
        >
          <span className="shrink-0 text-muted-foreground">{label}</span>
          <span className="min-w-0 text-right break-words">{value}</span>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-border p-2.5">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-semibold tabular-nums">{value}</div>
      {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
